<script setup>
import { Swiper, SwiperSlide } from 'swiper/vue/swiper-vue';
import SwiperCore, { Navigation, Keyboard } from 'swiper';
import 'swiper/scss';
import { DsBlock, DsButton, DsIconButton, DsText } from '@coloplast/design-system';
import MarketingCard from './marketingCard.vue';
import ProductCard from './productCard.vue';
import { reactive, ref, defineExpose, onMounted } from 'vue';
import { track_view_item_list } from 'shared/services/tracking/productTrackingService';
import { initIntersectionObserver } from 'shared/composables/observe';
import { itemListIdAndName } from 'shared/settings/constants';
import { useStore } from 'vuex';

SwiperCore.use([Navigation, Keyboard]);

const props = defineProps({
	vm: Object,
});

const store = useStore();

const cardSliderGap = 24;

const swiperOptions = reactive({
	watchOverflow: true,
	speed: 500,
	keyboard: true,
	navigation: {
		nextEl: `.next-${props.vm.BaseViewModel.CurrentBlockId}`,
		prevEl: `.prev-${props.vm.BaseViewModel.CurrentBlockId}`,
	},
	breakpoints: {
		1024: {
			slidesPerView: 4,
			slidesPerGroup: 4,
			spaceBetween: cardSliderGap,
		},
		768: {
			slidesPerView: 3,
			slidesPerGroup: 3,
			spaceBetween: cardSliderGap,
		},
		580: {
			slidesPerView: 2.2,
			slidesPerGroup: 2,
			spaceBetween: cardSliderGap,
		},
		0: {
			slidesPerView: 1.2,
			slidesPerGroup: 1,
			spaceBetween: cardSliderGap,
		},
	},
});

const swiper = ref(null);
const isBeginning = ref(false);
const isEnd = ref(false);

const onSwiper = (swiperInstance) => {
	swiper.value = swiperInstance;

	const updateNavigationStatus = () => {
		isBeginning.value = swiperInstance.isBeginning;
		isEnd.value = swiperInstance.isEnd;
	};

	updateNavigationStatus();

	swiperInstance?.on('slideChange', updateNavigationStatus);
	swiperInstance?.on('resize', updateNavigationStatus);
};
defineExpose({
	onSwiper,
});

const productCardEl = ref(null);

const { removeFromObserver, addToObserver } = initIntersectionObserver(
	(productElements) => {
		const observedProducts = [];

		productElements.forEach(({ target, isIntersecting }) => {
			if (!isIntersecting) {
				return;
			}
			removeFromObserver(target);
			// NOTE: if there's a marketing card inside the slider, although the product is eg. second
			// consecutive product, the marketing card before it, causes it's index to be 2 instead of 1
			const i = Number(target.dataset.index);
			const card = props.vm.BaseViewModel.Cards[i];
			card.globalTrackingParameters.index = i;
			observedProducts.push(card);
		});

		if (observedProducts.length > 0) {
			track_view_item_list({
				productsDto: props.vm.BaseViewModel,
				observedProducts: observedProducts,
				itemListIdAndName: itemListIdAndName.CARD_SLIDER_BLOCK,
			});
		}
	},
	{ threshold: 0.05 }
);

onMounted(() => {
	productCardEl.value.forEach((el) => {
		addToObserver(el.$el);
	});
	//need it to add the custom behaviour for product cards and for it's "add item to basket"
	store.commit('setIsProductCardOnCampaignPage', true);
});
</script>

<template>
	<DsBlock
		class="c-card-slider-block"
		:style="`--card-slider-gap: ${cardSliderGap}px;`"
		:background-color="vm.CurrentBlock.BackgroundColor"
	>
		<div class="c-card-slider-block__container">
			<header class="c-card-slider-block__header">
				<div>
					<DsText
						v-if="vm.CurrentBlock.Header"
						html-element="h2"
						text-style="medium"
						text-type="heading-3xl"
						class="c-card-slider-block__title"
					>
						{{ vm.CurrentBlock.Header }}
					</DsText>
					<DsText
						v-if="vm.CurrentBlock.Description"
						html-element="div"
						text-style="regular"
						text-type="body-md"
						class="c-card-slider-block__description"
					>
						{{ vm.CurrentBlock.Description }}
					</DsText>
				</div>
				<nav class="c-card-slider-block__navigation" v-if="!isBeginning || !isEnd">
					<div class="slide-prev-btn" :class="`prev-${vm.BaseViewModel.CurrentBlockId}`">
						<DsIconButton
							aria-label="Icon button previous slide"
							icon-name="chevron-left"
							size="md"
							:disabled="isBeginning"
						/>
					</div>
					<div class="slide-next-btn" :class="`next-${vm.BaseViewModel.CurrentBlockId}`">
						<DsIconButton
							aria-label="Icon button next slide"
							icon-name="chevron-right"
							size="md"
							:disabled="isEnd"
						/>
					</div>
				</nav>
			</header>

			<Swiper
				class="c-card-slider-block__items"
				ref="swiper"
				v-bind="swiperOptions"
				@swiper="onSwiper"
				data-testid="swiper"
			>
				<SwiperSlide
					class="c-card-slider-block__item"
					v-for="(card, index) in vm.BaseViewModel.Cards"
					:key="index"
				>
					<MarketingCard v-if="card.Type == 'Marketing'" :properties="card" />
					<ProductCard v-else :properties="card" :index="index" ref="productCardEl" />
				</SwiperSlide>
			</Swiper>
			<footer class="c-card-slider-block__footer">
				<DsButton
					v-if="vm.CurrentBlock.SeeAllUrl"
					:href="vm.CurrentBlock.SeeAllUrl"
					icon-end="navigate-forward"
					data-testid="seeAllLink"
					variant="link"
					target="_self"
					size="md"
				>
					{{ vm.CurrentBlock.SeeAllLabel }}
				</DsButton>
			</footer>
		</div>
	</DsBlock>
</template>

<style lang="scss">
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-card-slider-block {
	&__header {
		display: flex;
		justify-content: space-between;
		align-items: center;

		a {
			display: none;

			@include t-respond-to-min('medium') {
				display: inline-flex;
			}
		}
	}

	&__title {
		margin-block-end: 0;
	}

	&__description {
		color: ds-color('fg', 'muted');
	}

	&__items {
		margin-block-start: 24px;
		padding-inline-start: 6px !important;
		padding-inline-end: 6px !important;
		margin-inline-start: -6px !important;
		margin-inline-end: -6px !important;
	}

	&__item {
		block-size: auto; // Remove height:100% from swiper slide causing issues with the equal height of the cards
	}

	&__navigation {
		display: grid;
		grid-template-columns: auto auto;
		justify-content: end;
		gap: 16px;

		@include t-respond-to-min('medium') {
			margin-block-start: var(--card-slider-gap);
		}
	}

	&__footer {
		margin-block-start: var(--card-slider-gap);
		@include t-respond-to-min('medium') {
			text-align: end;
		}
	}
}
</style>
